import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ListingEntry } from 'graphql/listings/queries';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { AutoTemplate, NewTemplateType } from 'types/auto-templates';
import { listingItemtype } from 'types/ledger';

import { paneMaxHeight } from '../utils/pane-height';
import { FormTemplate } from './form-template';

const useStyles = makeStyles((theme: any) => ({
  paper: {
    padding: theme.spacing(2),
    minWidth: 0,
    ...paneMaxHeight(theme),
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  header: {
    gap: theme.spacing(1),
  },
  titleGroup: {
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  touchTarget: {
    minHeight: theme.spacing(5.5),
  },
  backButton: {
    minWidth: theme.spacing(5.5),
    minHeight: theme.spacing(5.5),
  },
  title: {
    overflowWrap: 'anywhere',
  },
}));

type TemplateSelectedProps = {
  setOpenEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  onBack: () => void;
  showBackAction: boolean;
  handleUpdateTemplate: (data: NewTemplateType) => Promise<boolean>;
  template: AutoTemplate;
  t: TFunction;
  partyRoles: ListingEntry[],
  transactionTypes: ListingEntry[],
  types: ListingEntry[],
  workflows: ListingEntry[];
  isSubmitting: boolean;
  listingsReady: boolean;
};

export const TemplateSelected: FC<TemplateSelectedProps> = ({
  template,
  handleUpdateTemplate,
  setOpenEditModal,
  onBack,
  showBackAction,
  t,
  partyRoles,
  transactionTypes,
  types,
  workflows,
  isSubmitting,
  listingsReady,
}) => {
  const classes = useStyles();

  return (
    <Paper className={classes.paper}>
      <Box className={classes.header} display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" marginBottom={4}>
        <Box className={classes.titleGroup} display="flex" alignItems="center" minWidth={0}>
          {showBackAction && (
            <IconButton
              className={classes.backButton}
              color="primary"
              aria-label={t('documents:back-to-templates')}
              title={t('documents:back-to-templates')}
              onClick={onBack}
              size="large"
            >
              <FontAwesomeIcon icon="arrow-left" />
            </IconButton>
          )}
          <Typography className={classes.title} variant="h6">
            {`${t('documents:template')} - ${template.name}`}
          </Typography>
        </Box>
        <Button
          className={classes.touchTarget}
          color="secondary"
          variant="outlined"
          onClick={() => setOpenEditModal(true)}
          disabled={isSubmitting}
        >
          <Typography variant="subtitle2">
            {t('documents:edit-document')}
          </Typography>
        </Button>
      </Box>
      <FormTemplate
        template={template}
        handleOnSubmit={handleUpdateTemplate}
        partyRoles={partyRoles}
        transactionTypes={transactionTypes}
        types={types}
        workflows={workflows}
        isSubmitting={isSubmitting}
        listingsReady={listingsReady}
      />
    </Paper>
  );
};
